import { useEffect, useRef } from 'react';

// Selector that matches every keyboard-focusable element. Excludes
// the disabled / tabindex=-1 cases so we don't accidentally land on
// a button that visually looks active but isn't.
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

/**
 * Traps keyboard focus inside the referenced container while `open`
 * is true. Three things in one hook:
 *
 *   1. On open: focus the first focusable element inside the container
 *      (or the container itself if nothing's focusable). The
 *      previously-focused element is remembered.
 *   2. While open: Tab and Shift-Tab cycle within the container.
 *      Anything outside the container is unreachable by keyboard.
 *   3. On close: restore focus to the element that had it before the
 *      trap engaged — typical "click button to open modal, modal
 *      closes, button is focused again" UX.
 *
 * Escape is handled here too: if `onClose` is provided, pressing
 * Escape inside the trap fires it. Saves every modal from duplicating
 * the same `keydown` listener.
 *
 * Usage:
 *
 *   const dialogRef = useRef(null);
 *   useFocusTrap(dialogRef, open, onClose);
 *   return open ? <div ref={dialogRef} role="dialog">…</div> : null;
 */
export function useFocusTrap(containerRef, open, onClose) {
  const previouslyFocused = useRef(null);

  useEffect(() => {
    if (!open) return;
    const container = containerRef.current;
    if (!container) return;

    previouslyFocused.current = document.activeElement;

    // Move focus into the modal. requestAnimationFrame avoids the
    // case where the container hasn't been painted yet and
    // querySelectorAll returns an empty list.
    requestAnimationFrame(() => {
      const focusables = container.querySelectorAll(FOCUSABLE);
      if (focusables.length > 0) {
        focusables[0].focus();
      } else {
        // Fallback so screen-reader users at least land inside the
        // dialog instead of staying on the trigger.
        container.setAttribute('tabindex', '-1');
        container.focus();
      }
    });

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && typeof onClose === 'function') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      const focusables = Array.from(container.querySelectorAll(FOCUSABLE))
        .filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      // Shift-Tab from the first → wrap to last. Tab from last →
      // wrap to first. Browser's native focus cycle would otherwise
      // jump out into the page background.
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);

      // Restore focus to wherever it was before the modal opened.
      // Guard against the element being gone (e.g. the modal was
      // opened from a button that has since unmounted).
      const prev = previouslyFocused.current;
      if (prev && typeof prev.focus === 'function' && document.contains(prev)) {
        prev.focus();
      }
    };
  }, [open, containerRef, onClose]);
}
